import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import {
  AppBar,
  Box,
  Toolbar,
  Typography,
  Button,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  Menu,
  MenuItem,
  Avatar,
  alpha,
} from '@mui/material';
import { styled, keyframes } from '@mui/material/styles';
import MenuIcon from '@mui/icons-material/Menu';
import HomeIcon from '@mui/icons-material/Home';
import HomeRoundedIcon from '@mui/icons-material/HomeRounded';
import YardIcon from '@mui/icons-material/Yard';
import LocalFloristIcon from '@mui/icons-material/LocalFlorist';
import ListAltIcon from '@mui/icons-material/ListAlt';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import LogoutIcon from '@mui/icons-material/Logout';
import SettingsIcon from '@mui/icons-material/Settings';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import EventNoteIcon from '@mui/icons-material/EventNote';
import BuildIcon from '@mui/icons-material/Build';
import WaterDropIcon from '@mui/icons-material/WaterDrop';
import FavoriteIcon from '@mui/icons-material/Favorite';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import ScienceIcon from '@mui/icons-material/Science';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DescriptionIcon from '@mui/icons-material/Description';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import BiotechIcon from '@mui/icons-material/Biotech';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import StorefrontIcon from '@mui/icons-material/Storefront';
import { useAuth } from '../../contexts/AuthContext';
import MobileBottomNav from './MobileBottomNav';
import QuickPlantIdentifier from '../tools/QuickPlantIdentifier';
import PlantAnalyzer from '../tools/PlantAnalyzer';
// Logó most a public könyvtárból töltődik be

// Animációk
const logoFloat = keyframes`
  0%, 100% {
    transform: translateY(0px);
  }
  50% {
    transform: translateY(-2px);
  }
`;



// Styled komponensek
const StyledAppBar = styled(AppBar)(({ theme }) => ({
  background: `
    linear-gradient(135deg, 
      ${theme.palette.primary.main} 0%, 
      ${theme.palette.primary.dark} 50%,
      ${alpha(theme.palette.primary.main, 0.9)} 100%
    )
  `,
  backdropFilter: 'blur(10px)',
  borderBottom: `1px solid ${alpha(theme.palette.common.white, 0.1)}`,
  boxShadow: `
    0 8px 32px ${alpha(theme.palette.primary.main, 0.3)},
    0 2px 8px ${alpha(theme.palette.common.black, 0.1)}
  `,
  position: 'relative',
}));

const LogoContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  textDecoration: 'none',
  color: 'inherit',
  transition: 'all 0.3s ease',
  padding: theme.spacing(0.5, 1),
  borderRadius: theme.spacing(1),
  '&:hover': {
    backgroundColor: alpha(theme.palette.common.white, 0.1),
    transform: 'translateY(-1px)',
    '& img': {
      animation: `${logoFloat} 2s ease-in-out infinite`,
    }
  },
  '& img': {
    filter: 'drop-shadow(0 2px 8px rgba(0, 0, 0, 0.2))',
    transition: 'all 0.3s ease',
  }
}));

const BrandText = styled(Typography)(({ theme }) => ({
  fontWeight: 700,
  fontSize: '1.5rem',
  background: `linear-gradient(135deg, 
    ${theme.palette.common.white} 0%, 
    ${alpha(theme.palette.common.white, 0.9)} 100%
  )`,
  backgroundClip: 'text',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  textShadow: `0 2px 4px ${alpha(theme.palette.common.black, 0.2)}`,
  letterSpacing: '0.5px',
}));

const NavButton = styled(Button, { shouldForwardProp: (prop) => prop !== 'active' })(({ theme }) => ({
  color: theme.palette.common.white,
  fontWeight: 600,
  padding: theme.spacing(1, 2),
  minHeight: 44,
  whiteSpace: 'nowrap',
  flexShrink: 0,
  borderRadius: theme.spacing(3),
  textTransform: 'none',
  transition: 'all 0.3s ease',
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: '-100%',
    width: '100%',
    height: '100%',
    background: `linear-gradient(90deg, 
      transparent, 
      ${alpha(theme.palette.common.white, 0.2)}, 
      transparent
    )`,
    transition: 'left 0.5s ease',
  },
  '&:hover': {
    backgroundColor: alpha(theme.palette.common.white, 0.15),
    transform: 'translateY(-1px)',
    boxShadow: `0 4px 12px ${alpha(theme.palette.common.black, 0.2)}`,
    '&::before': {
      left: '100%',
    }
  },
  '&.active': {
    backgroundColor: alpha(theme.palette.common.white, 0.2),
    boxShadow: `0 2px 8px ${alpha(theme.palette.common.black, 0.2)}`,
  }
}));

const UserAvatar = styled(Avatar)(({ theme }) => ({
  width: 36,
  height: 36,
  backgroundColor: theme.palette.secondary.main,
  border: `2px solid ${alpha(theme.palette.common.white, 0.3)}`,
  transition: 'all 0.3s ease',
  cursor: 'pointer',
  '&:hover': {
    transform: 'scale(1.1)',
    boxShadow: `0 4px 12px ${alpha(theme.palette.common.black, 0.3)}`,
    borderColor: alpha(theme.palette.common.white, 0.5),
  }
}));

const StyledDrawer = styled(Drawer)(({ theme }) => ({
  '& .MuiDrawer-paper': {
    background: theme.palette.background.paper,
    backdropFilter: 'blur(10px)',
    borderRight: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
    boxShadow: `0 8px 32px ${alpha(theme.palette.common.black, 0.15)}`,
    // Ensure solid background on mobile for better readability
    '@media (max-width: 600px)': {
      background: theme.palette.background.paper,
      opacity: 1,
    }
  }
}));

const DrawerHeader = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  background: `linear-gradient(135deg, 
    ${alpha(theme.palette.primary.main, 0.15)} 0%, 
    ${alpha(theme.palette.primary.main, 0.08)} 100%
  )`,
  borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
  // Ensure better contrast on mobile
  '@media (max-width: 600px)': {
    background: alpha(theme.palette.primary.main, 0.1),
    borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
  }
}));

const DrawerListItem = styled(ListItemButton)(({ theme }) => ({
  margin: theme.spacing(0.5, 1),
  borderRadius: theme.spacing(1),
  transition: 'all 0.3s ease',
  minHeight: 48, // Ensure minimum touch target size on mobile
  '&:hover': {
    backgroundColor: alpha(theme.palette.primary.main, 0.12),
    transform: 'translateX(4px)',
  },
  '&.Mui-selected': {
    backgroundColor: alpha(theme.palette.primary.main, 0.18),
    color: theme.palette.primary.main,
    fontWeight: 600,
    '&:hover': {
      backgroundColor: alpha(theme.palette.primary.main, 0.25),
    }
  },
  // Enhanced mobile styles
  '@media (max-width: 600px)': {
    minHeight: 52,
    margin: theme.spacing(0.5, 1.5),
    '& .MuiListItemText-primary': {
      fontSize: '1rem',
      fontWeight: 500,
    },
    '& .MuiListItemIcon-root': {
      minWidth: 40,
    }
  }
}));

const LoginButton = styled(Button)(({ theme }) => ({
  color: theme.palette.common.white,
  borderColor: alpha(theme.palette.common.white, 0.5),
  fontWeight: 600,
  padding: theme.spacing(1, 3),
  minHeight: 44,
  whiteSpace: 'nowrap',
  borderRadius: theme.spacing(3),
  textTransform: 'none',
  transition: 'all 0.3s ease',
  '&:hover': {
    backgroundColor: alpha(theme.palette.common.white, 0.1),
    borderColor: theme.palette.common.white,
    transform: 'translateY(-1px)',
    boxShadow: `0 4px 12px ${alpha(theme.palette.common.black, 0.2)}`,
  }
}));

const Header: React.FC = () => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [toolsMenuAnchor, setToolsMenuAnchor] = useState<null | HTMLElement>(null);
  const toolsMenuOpen = Boolean(toolsMenuAnchor);
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Profilkép URL ellenőrzése (debug removed for production)

  const handleDrawerToggle = () => {
    setDrawerOpen(!drawerOpen);
  };

  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    logout();
    handleProfileMenuClose();
    navigate('/');
  };

  const handleProfileClick = () => {
    handleProfileMenuClose();
    navigate('/profile');
  };

  const handleAdminSettingsClick = () => {
    handleProfileMenuClose();
    navigate('/admin');
  };

  const handleNavigation = (path: string) => {
    navigate(path);
    setDrawerOpen(false);
  };

  const handleToolsMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setToolsMenuAnchor(event.currentTarget);
  };

  const handleToolsMenuClose = () => {
    setToolsMenuAnchor(null);
  };

  const handleToolsMenuItemClick = (path: string) => {
    navigate(path);
    setToolsMenuAnchor(null);
  };

  // Menüelem típus definíció
  interface MenuItem {
    text: string;
    icon: React.ReactElement;
    path: string;
    requireAuth?: boolean;
    adminOnly?: boolean;
  }

  // Főmenü elemek - ezek jelennek meg a fő navigációs sávban (lg-től).
  // A Főoldal a logóval érhető el, az ÁSZF a láblécben, az Admin a profilmenüben.
  const mainMenuItems: MenuItem[] = [
    { text: 'Kertek', icon: <YardIcon />, path: '/gardens', requireAuth: true },
    { text: 'Növények', icon: <LocalFloristIcon />, path: '/plants' },
    { text: 'Növénylistáim', icon: <ListAltIcon />, path: '/plant-lists', requireAuth: true },
    { text: 'Kertésznaptár', icon: <EventNoteIcon />, path: '/garden-calendar', requireAuth: true },
  ];

  // „Több” menü elemei - ritkábban használt oldalak
  const toolsMenuItems: MenuItem[] = [
    { text: 'Szobanövény gondozó', icon: <HomeRoundedIcon />, path: '/indoor-plants', requireAuth: true },
    { text: 'Kerti eszközök', icon: <BuildIcon />, path: '/garden-tools', requireAuth: true },
    { text: 'Permetszer adatbázis', icon: <ScienceIcon />, path: '/pesticide-database', requireAuth: true },
    { text: 'Permetező lista', icon: <WaterDropIcon />, path: '/sprayer-list', requireAuth: true },
    { text: 'Kedvenc szerek', icon: <FavoriteIcon />, path: '/pesticide-favorites', requireAuth: true },
    { text: 'Szer összehasonlítás', icon: <CompareArrowsIcon />, path: '/pesticide-comparison', requireAuth: true },
    { text: 'Kertészeteknek', icon: <StorefrontIcon />, path: '/kerteszet', requireAuth: true },
  ];

  // Összes menüelem (mobil drawer számára)
  const allMenuItems: MenuItem[] = [
    { text: 'Főoldal', icon: <HomeIcon />, path: '/' },
    ...mainMenuItems,
    ...toolsMenuItems,
    { text: 'ÁSZF', icon: <DescriptionIcon />, path: '/aszf' },
    { text: 'Admin Panel', icon: <SettingsIcon />, path: '/admin', requireAuth: true, adminOnly: true },
  ];

  // Drawer state for quick plant identifier
  const [drawerPlantIdOpen, setDrawerPlantIdOpen] = useState(false);
  // Drawer state for plant analyzer
  const [drawerPlantAnalyzerOpen, setDrawerPlantAnalyzerOpen] = useState(false);

  const drawer = (
    <Box sx={{ width: 250 }}>
      <DrawerHeader>
        <img
          src="/logo_tb.png"
          alt="KertMagad logó"
          style={{ height: '32px', width: 'auto' }}
        />
        <Typography variant="h6" component="div" sx={{ fontWeight: 600 }}>
          KertMagad
        </Typography>
      </DrawerHeader>
      <Divider />
      {/* Gyors növényfelismerő és növényelemző gombok a drawer tetején */}
      {user && (
        <>
          <Box sx={{ p: 2 }}>
            <Button
              fullWidth
              variant="contained"
              color="success"
              startIcon={<CameraAltIcon />}
              onClick={() => {
                setDrawerOpen(false);
                setDrawerPlantIdOpen(true);
              }}
              sx={{
                py: 1.5,
                borderRadius: 2,
                fontWeight: 600,
                textTransform: 'none',
                mb: 1,
              }}
            >
              Növény Felismerés
            </Button>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', mb: 1.5 }}>
              Hosszan nyomva = gyors kamera
            </Typography>
            <Button
              fullWidth
              variant="contained"
              color="warning"
              startIcon={<BiotechIcon />}
              onClick={() => {
                setDrawerOpen(false);
                setDrawerPlantAnalyzerOpen(true);
              }}
              sx={{
                py: 1.5,
                borderRadius: 2,
                fontWeight: 600,
                textTransform: 'none',
              }}
            >
              Növény Elemző
            </Button>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', mt: 0.5 }}>
              Növényorvosi elemzés
            </Typography>
          </Box>
          <Divider />
        </>
      )}
      <List>
        {allMenuItems.map((item) => (
          (!item.requireAuth || user) && (!item.adminOnly || user?.role === 'admin') && (
            <ListItem key={item.text} disablePadding>
              <DrawerListItem
                onClick={() => handleNavigation(item.path)}
                selected={location.pathname === item.path}
              >
                <ListItemIcon>{item.icon}</ListItemIcon>
                <ListItemText primary={item.text} />
              </DrawerListItem>
            </ListItem>
          )
        ))}
      </List>
    </Box>
  );

  return (
    <>
    <StyledAppBar position="static">
      <Toolbar>
        <IconButton
          color="inherit"
          aria-label="open drawer"
          edge="start"
          onClick={handleDrawerToggle}
          sx={{ 
            mr: 2, 
            width: 44,
            height: 44,
            display: { lg: 'none' },
            backgroundColor: alpha('#ffffff', 0.1),
            border: `1px solid ${alpha('#ffffff', 0.2)}`,
            borderRadius: 2,
            transition: 'all 0.3s ease',
            '&:hover': {
              backgroundColor: alpha('#ffffff', 0.2),
              borderColor: alpha('#ffffff', 0.4),
              transform: 'scale(1.05)',
              boxShadow: `0 4px 12px ${alpha('#000000', 0.2)}`,
            }
          }}
        >
          <MenuIcon sx={{ fontSize: '1.5rem' }} />
        </IconButton>
        <Link to="/" style={{ textDecoration: 'none', color: 'inherit', flexGrow: 1 }}>
          <LogoContainer>
            <img 
              src="/logo_tb.png" 
              alt="KertMagad logó" 
              style={{ height: '40px', width: 'auto' }}
            />
            <BrandText variant="h6">
              KertMagad
            </BrandText>
          </LogoContainer>
        </Link>
        <Box
          component="nav"
          aria-label="Fő navigáció"
          data-testid="desktop-nav"
          sx={{ display: { xs: 'none', lg: 'flex' }, gap: 0.5, alignItems: 'center', flexWrap: 'nowrap' }}
        >
          {/* Főmenü elemek */}
          {mainMenuItems.map((item) => (
            (!item.requireAuth || user) && (
              <Link key={item.text} to={item.path} style={{ textDecoration: 'none' }}>
                <NavButton
                  startIcon={item.icon}
                  className={location.pathname === item.path ? 'active' : ''}
                >
                  {item.text}
                </NavButton>
              </Link>
            )
          ))}
          
          {/* „Több” menü (szobanövények, kerti eszközök, permetszerek) - csak bejelentkezett felhasználóknak */}
          {user && (
            <>
              <NavButton
                onClick={handleToolsMenuOpen}
                endIcon={<ExpandMoreIcon />}
                startIcon={<MoreHorizIcon />}
                id="tools-button"
                className={toolsMenuItems.some(item => location.pathname === item.path) ? 'active' : ''}
                aria-controls={toolsMenuOpen ? 'tools-menu' : undefined}
                aria-haspopup="true"
                aria-expanded={toolsMenuOpen ? 'true' : undefined}
              >
                Több
              </NavButton>
              <Menu
                id="tools-menu"
                anchorEl={toolsMenuAnchor}
                open={toolsMenuOpen}
                onClose={handleToolsMenuClose}
                MenuListProps={{
                  'aria-labelledby': 'tools-button',
                }}
                PaperProps={{
                  sx: {
                    mt: 1,
                    borderRadius: 2,
                    minWidth: 220,
                    boxShadow: (theme) => 
                      `0 8px 32px ${alpha(theme.palette.common.black, 0.2)}`,
                    border: (theme) => 
                      `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                  }
                }}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'left',
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'left',
                }}
              >
                {toolsMenuItems.map((item) => (
                  <MenuItem
                    key={item.text}
                    onClick={() => handleToolsMenuItemClick(item.path)}
                    selected={location.pathname === item.path}
                    sx={{ 
                      gap: 1,
                      minHeight: 48,
                      '&.Mui-selected': {
                        backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.1),
                        color: (theme) => theme.palette.primary.main,
                        fontWeight: 600,
                      }
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      {item.icon}
                    </ListItemIcon>
                    <ListItemText primary={item.text} />
                  </MenuItem>
                ))}
              </Menu>
            </>
          )}

        </Box>
        {user ? (
          <>
            {/* Gyors növényfelismerő és elemző gombok - mobilon a hamburger menüben érhetők el */}
            <Box
              sx={{
                mx: 1,
                display: { xs: 'none', sm: 'flex' },
                gap: 1,
                flexShrink: 0,
                '& .MuiIconButton-root': { minWidth: 44, minHeight: 44 },
              }}
            >
              <QuickPlantIdentifier />
              <PlantAnalyzer />
            </Box>
            <IconButton
              onClick={handleProfileMenuOpen}
              color="inherit"
              aria-controls="profile-menu"
              aria-haspopup="true"
              aria-label="Felhasználói menü"
              sx={{ ml: 1, width: 48, height: 48, flexShrink: 0 }}
              data-testid="user-menu"
            >
              <UserAvatar src={user.profileImageUrl || undefined}>
                {!user.profileImageUrl && user.username.charAt(0).toUpperCase()}
              </UserAvatar>
            </IconButton>
            <Menu
              id="profile-menu"
              anchorEl={anchorEl}
              keepMounted
              open={Boolean(anchorEl)}
              onClose={handleProfileMenuClose}
            >
              <MenuItem onClick={handleProfileClick} sx={{ minHeight: 44 }}>
                <ListItemIcon>
                  <AccountCircleIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Profil</ListItemText>
              </MenuItem>
              {user.role === 'admin' && (
                <MenuItem onClick={handleAdminSettingsClick} sx={{ minHeight: 44 }}>
                  <ListItemIcon>
                    <SmartToyIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>Admin Panel</ListItemText>
                </MenuItem>
              )}
              <MenuItem onClick={handleLogout} sx={{ minHeight: 44 }}>
                <ListItemIcon>
                  <LogoutIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Kijelentkezés</ListItemText>
              </MenuItem>
            </Menu>
          </>
        ) : (
          <Link to="/login" style={{ textDecoration: 'none' }}>
            <LoginButton variant="outlined">
              Bejelentkezés
            </LoginButton>
          </Link>
        )}
      </Toolbar>
      <StyledDrawer
        variant="temporary"
        open={drawerOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true,
        }}
        sx={{
          display: { xs: 'block', lg: 'none' },
          '& .MuiDrawer-paper': { boxSizing: 'border-box', width: 250 },
        }}
      >
        {drawer}
      </StyledDrawer>
      {/* Drawer-ből indított növényfelismerő dialógus */}
      {user && (
        <QuickPlantIdentifier
          dialogOnly
          externalOpen={drawerPlantIdOpen}
          onExternalOpenChange={setDrawerPlantIdOpen}
        />
      )}
      {/* Drawer-ből indított növényelemző dialógus */}
      {user && (
        <PlantAnalyzer
          dialogOnly
          externalOpen={drawerPlantAnalyzerOpen}
          onExternalOpenChange={setDrawerPlantAnalyzerOpen}
        />
      )}
    </StyledAppBar>
    {/* Mobil alsó navigációs sáv (md alatt, bejelentkezve, nem a szerkesztőben) */}
    <MobileBottomNav onMore={() => setDrawerOpen(true)} moreOpen={drawerOpen} />
    </>
  );
};

export default Header; 